import { useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../../../services/api';

/**
 * Custom hook for managing invite codes
 *
 * @returns {Object} Invite codes state and functions
 * @returns {Array} inviteCodes - List of invite codes
 * @returns {boolean} loading - Loading state for invite codes operations
 * @returns {Function} fetchInviteCodes - Fetches all invite codes from the API
 * @returns {Function} generateInviteCode - Generates a new invite code with provided form data
 * @returns {Function} copyToClipboard - Copies text to clipboard and shows success toast
 *
 * @example
 * const {
 *   inviteCodes,
 *   loading,
 *   fetchInviteCodes,
 *   generateInviteCode,
 *   copyToClipboard
 * } = useInviteCodes();
 */
const useInviteCodes = () => {
  const [inviteCodes, setInviteCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * Fetches all invite codes from the API
   * Updates the inviteCodes state with the fetched data
   * Shows error toast if the fetch fails
   */
  const fetchInviteCodes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/invite-codes');
      if (response.success) {
        setInviteCodes(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching invite codes:', error);
      toast.error('Failed to load invite codes');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generates a new invite code with the provided form data
   *
   * @param {Object} inviteCodeForm - Form data for generating invite code
   * @param {string} inviteCodeForm.userType - Type of user ('referral', 'agent', etc.)
   * @param {number} inviteCodeForm.maxUses - Maximum number of times the code can be used
   * @param {number} inviteCodeForm.expiresInDays - Number of days until the code expires
   * @param {string} inviteCodeForm.description - Description of the invite code
   * @param {string} inviteCodeForm.campaign - Campaign name (optional)
   * @returns {Promise<Object|null>} The generated invite code data or null if failed
   */
  const generateInviteCode = async (inviteCodeForm) => {
    setLoading(true);
    try {
      const response = await api.post('/invite-codes/generate', inviteCodeForm);
      if (response.success) {
        toast.success(`Invite code generated: ${response.data.code}`);
        await fetchInviteCodes();
        return response.data;
      }
      return null;
    } catch (error) {
      toast.error(error.message || 'Failed to generate code');
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Copies the provided text to the clipboard
   * Shows a success toast notification
   *
   * @param {string} text - Text to copy to clipboard
   */
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  return {
    inviteCodes,
    loading,
    fetchInviteCodes,
    generateInviteCode,
    copyToClipboard
  };
};

export default useInviteCodes;
